import { motion } from 'framer-motion';
import { Award, Clock, Heart, Users, CheckCircle } from 'lucide-react';

export default function About() {
  const achievements = [
    { icon: <Award className="w-6 h-6" />, title: 'BDS Qualification', desc: 'Bachelor of Dental Surgery' },
    { icon: <Award className="w-6 h-6" />, title: 'BPS Certified', desc: 'Certified BPS Denture Specialist' },
    { icon: <Clock className="w-6 h-6" />, title: '16+ Years', desc: 'Clinical Experience' },
    { icon: <Users className="w-6 h-6" />, title: '5000+', desc: 'Happy Patients' }
  ];

  const values = [
    'Precision and Excellence',
    'Patient-Centric Approach',
    'Continuous Learning',
    'Ethical Practice',
    'Compassionate Care',
    'Modern Technology'
  ];

  return (
    <div data-testid="about-page">
      <section className="pt-32 pb-20 bg-gradient-to-r from-blue-50 to-cyan-50" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-bold text-primary uppercase tracking-widest">About Us</span>
            <h1 className="font-heading font-bold text-5xl sm:text-6xl text-slate-900 mt-4 mb-6">
              Excellence in Dental Care
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Dr. Chandrashekhar Harale brings expertise, precision, and compassionate care to every patient
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="doctor-profile">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1631596577204-53ad0d6e6978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxmcmllbmRseSUyMGRlbnRpc3QlMjB0cmVhdGluZyUyMHBhdGllbnQlMjBzbWlsZSUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzY4Nzc0NzU3fDA&ixlib=rb-4.1.0&q=85"
                  alt="Dr. Chandrashekhar Harale"
                  className="rounded-3xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-slate-900">BPS</p>
                      <p className="text-sm text-slate-600">Certified</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-bold text-primary uppercase tracking-widest">Meet Your Dentist</span>
              <h2 className="font-heading font-bold text-4xl text-slate-900 mt-4 mb-6">
                Dr. Chandrashekhar Harale
                <span className="block text-2xl text-slate-600 font-normal mt-2">(BDS)</span>
              </h2>

              <div className="space-y-6 text-slate-600 leading-relaxed">
                <p className="text-lg">
                  With over 16 years of dedicated practice in dentistry, Dr. Chandrashekhar Harale has established himself as a trusted dental care provider in Kolhapur. His commitment to excellence and patient satisfaction has earned him the respect of thousands of patients.
                </p>
                <p className="text-lg">
                  Dr. Harale holds a Bachelor of Dental Surgery (BDS) degree and is a Certified BPS Denture Specialist, bringing specialized expertise in advanced denture solutions. His clinic provides a comfortable, clean, and contemporary setting where patients receive precise care and professionalism.
                </p>
                <p className="text-lg">
                  Whether it's routine check-ups, cleanings, or more intricate procedures like BPS dentures, dental implants, and root canals, Dr. Harale ensures each treatment is administered with exceptional skill and dedication. His approach focuses on making every dental visit easy, stress-free, and patient-centered.
                </p>
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-2xl">
                <p className="text-primary font-semibold text-lg italic">
                  "My mission is to provide trustworthy, patient-focused, and high-quality dental treatment that transforms lives through healthy, confident smiles."
                </p>
                <p className="text-slate-600 mt-2">- Dr. Chandrashekhar Harale</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="achievements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-4xl text-slate-900 mb-4">
              Qualifications & Achievements
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 text-center"
                data-testid={`achievement-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 text-primary mb-4">
                  {item.icon}
                </div>
                <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="clinic-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-sm font-bold text-primary uppercase tracking-widest">Our Philosophy</span>
              <h2 className="font-heading font-bold text-4xl text-slate-900 mt-4 mb-6">
                What Drives Us
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                At Vijaya Dental Clinic, we believe in creating lasting relationships with our patients through exceptional care, modern technology, and unwavering commitment to dental excellence.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {values.map((value, index) => (
                  <div key={index} className="flex items-center gap-3" data-testid={`value-${index}`}>
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1642844819197-5f5f21b89ff8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvciUyMGJyaWdodCUyMGNsZWFuJTIwcmVjZXB0aW9ufGVufDB8fHx8MTc2ODc3NDc1NXww&ixlib=rb-4.1.0&q=85"
                alt="Vijaya Dental Clinic"
                className="rounded-3xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900 text-white" data-testid="clinic-story">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-heading font-bold text-4xl mb-8">
              The Vijaya Dental Clinic Story
            </h2>
            <div className="space-y-6 text-lg text-slate-300 leading-relaxed">
              <p>
                Dr Harale's Vijaya Dental Clinic stands as a beacon of excellence in the realm of dental care. Located opposite Railway Station in New Shahupuri, Kolhapur, our clinic has been serving the community with dedication and expertise.
              </p>
              <p>
                We offer a wide array of services designed to cater to diverse oral health needs. From routine check-ups and tooth polishing to complex procedures like dental implants and BPS dentures, each service is delivered with utmost care and attention to detail.
              </p>
              <p>
                Our state-of-the-art facilities, combined with Dr. Harale's expertise and commitment to staying abreast of the latest advancements, ensure that patients receive the highest standard of treatment. We pride ourselves on creating a warm, comfortable environment where patients feel valued and cared for.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
